import * as THREE from 'three';
import React, { useEffect, useRef, type JSX } from 'react';
import { useGLTF, MeshTransmissionMaterial } from '@react-three/drei';
import type { GLTF } from 'three-stdlib';
import { useFrame } from '@react-three/fiber';
import { bezier, reMap } from '../../../util/math.utils';
import useWindowSize from '../../../hooks/useWindowSize';
import { isTouch } from '../../../hooks/useIsDesktopDevice';

type GLTFResult = GLTF & {
  nodes: {
    ['1']: THREE.Mesh;
    ['2']: THREE.Mesh;
    ['3']: THREE.Mesh;
    ['4']: THREE.Mesh;
  };
  materials: {
    ['RS Material.1']: THREE.MeshStandardMaterial;
    ['RS Material']: THREE.MeshStandardMaterial;
  };
};

type PropsType = {
  progressRef: React.MutableRefObject<number>;
  backgroundColor?: string;
  shapeColor?: string;
  homepage?: boolean;
  superpowerSlide?: boolean;
  isCurrent?: boolean;
};
export default function Pinwheel(props: PropsType): JSX.Element {
  const group = useRef<THREE.Group>(null);
  const { nodes, animations } = useGLTF('/assets/3d/Shapes/Pinwheel.glb') as unknown as GLTFResult;
  const { width } = useWindowSize();

  const mixer = useRef<THREE.AnimationMixer>(undefined);
  const meshGroupRef = useRef<THREE.Group>(null);

  useEffect(() => {
    if (!group.current) return;
    mixer.current = new THREE.AnimationMixer(group.current);
    const action = mixer.current.clipAction(animations[0]);
    action.play();
  }, [animations]);

  useFrame(() => {
    if (!mixer?.current || !props.progressRef || !props.isCurrent || !meshGroupRef.current) return;
    if (props.progressRef.current < 100) {
      const p = props.progressRef.current;
      const b = bezier(p / 100, 0, 0.5, 0.5, 1);
      const t_bezier = b * animations[0].duration;
      const t = (p * animations[0].duration) / 100;
      mixer.current.setTime(props.homepage ? t_bezier : t);

      // Makes sure pin shapes arent visible at the start:
      meshGroupRef.current.position.z = reMap(props.progressRef.current, 0, 100, 100, -50);

      // meshGroupRef.current.position.x = 0;
      // if (props.homepage) {
      // meshGroupRef.current.position.z = reMap(props.progressRef.current, 10, 100, 7.902, 7.8);
      // }
    }
  });

  const material = props.homepage ? (
    <MeshTransmissionMaterial
      backside
      // backsideThickness={1}
      // samples={16}
      // thickness={0.2}
      // anisotropicBlur={0.1}
      samples={1}
      iridescence={1}
      iridescenceIOR={0.7}
      backsideResolution={512}
      iridescenceThicknessRange={[0, 200]}
      envMapIntensity={0.7}
      background={new THREE.Color('#4C4744')}
      distortion={0}
      distortionScale={0}
      temporalDistortion={0}
      clearcoat={1}
    />
  ) : (
    <MeshTransmissionMaterial
      backside
      transparent
      samples={1}
      iridescence={isTouch ? 0 : 0.8}
      iridescenceIOR={0}
      backsideThickness={0}
      backsideResolution={isTouch ? 128 : 512}
      iridescenceThicknessRange={[0, 200]}
      clearcoat={0}
      envMapIntensity={isTouch ? 0.1 : 4}
      opacity={0.85}
      transmission={1}
      chromaticAberration={0}
      transmissionSampler={false}
      roughness={0}
      clearcoatRoughness={0}
      reflectivity={0}
      sheenColor="#fff"
      sheen={isTouch ? 0.6 : 0.6}
      metalness={0}
      ior={1}
      specularColor="white"
      specularIntensity={isTouch ? 0 : 0.3}
      attenuationColor="white"
      thickness={isTouch ? 0 : 0.2}
      anisotropicBlur={isTouch ? 0 : 0.1}
      distortion={0}
      distortionScale={0}
      temporalDistortion={0}
      background={new THREE.Color(props.shapeColor || '#4C4744')}
    />
  );

  return (
    <group ref={group} {...props} dispose={null} visible={props.isCurrent}>
      <group ref={meshGroupRef}>
        <group>
          <group
            name="Pinwheel"
            position={[0, 1, -500]}
            scale={Math.min(0.4, reMap(width, 320, 650, 0.275, 0.4))}
          >
            {props.homepage && (
              <mesh
                visible={false}
                geometry={nodes['1'].geometry}
                position={[-149.533, 42.054, 0]}
                rotation={[0.12, 0.897, 2.948]}
              >
                {material}
              </mesh>
            )}
            <mesh
              name="1"
              geometry={nodes['1'].geometry}
              position={[-149.533, 42.054, 0]}
              rotation={[0.12, 0.897, 2.948]}
            >
              {material}
            </mesh>
            <mesh
              name="2"
              geometry={nodes['2'].geometry}
              position={[137.746, 73.393, 187.902]}
              rotation={[-2.195, 0.109, -2.76]}
            >
              {material}
            </mesh>
            <mesh
              name="3"
              geometry={nodes['3'].geometry}
              position={[164.698, 204.833, 143.402]}
              rotation={[-2.44, 0.292, -1.203]}
            >
              {material}
            </mesh>
            <mesh
              name="4"
              geometry={nodes['4'].geometry}
              position={[-94.382, -96.64, -14.425]}
              rotation={[-2.644, 1.064, -2.417]}
            >
              {material}
            </mesh>
          </group>
        </group>
      </group>
    </group>
  );
}

useGLTF.preload('/assets/3d/Shapes/Pinwheel.glb');
